'use client';

import { useInfiniteQuery } from '@tanstack/react-query';
import {
  fetchContentModerationFindingsPage,
  provenanceKeys,
  type ContentModerationFindingsPage,
  type DetectionType,
} from '@/shared/cors-provenance-api';
import { isAllCasesSelection } from '@/shared/case-summary';
import { useProvenanceSession } from './useProvenanceSession';
import { useContentModerationCase } from './useContentModerationFolderName';

const DEFAULT_PAGE_SIZE = 25;

/**
 * TanStack ``useInfiniteQuery`` hook for
 * ``GET /provenance-service/v1/content-moderation/findings``.
 *
 * The endpoint uses cursor-based pagination (``nextToken``). Each page's
 * ``nextToken`` is passed as the ``pageParam`` for the subsequent fetch; the
 * query stops when ``nextToken`` is ``null``.
 *
 * List UIs typically render one page at a time via ``data.pages[pageIndex]``
 * and advance with ``fetchNextPage()`` / ``hasNextPage``. Review mode can
 * flatten via ``data?.pages.flatMap(p => p.items)`` after draining pages.
 *
 * Auth wiring follows the same pattern as every other provenance-service hook.
 * The query stays disabled until the session is ready and ``caseId`` resolves to
 * a folder segment — see {@link useContentModerationFolderName} for why the wire
 * ``case_id`` is the segment and not the display name. All Cases (``*``) is not
 * a case and never enables the query.
 */
export function useContentModerationFindings(
  caseId: string | null | undefined,
  opts: {
    /** Detection type to list findings for. Defaults to ``"CSAM"``. */
    detectionType?: DetectionType;
    /** Restrict to a single backend status value: ``"FLAGGED" | "REVIEWED" | "CONFIRMED"``. */
    status?: string;
    limit?: number;
    enabled?: boolean;
  } = {},
) {
  const { cellId, accessToken, enabled: sessionEnabled } = useProvenanceSession();
  const {
    detectionType = 'CSAM',
    status,
    limit = DEFAULT_PAGE_SIZE,
    enabled: optEnabled = true,
  } = opts;
  const { folderName, isResolving: isCaseResolving } = useContentModerationCase(caseId);
  const enabled =
    sessionEnabled && Boolean(folderName) && !isAllCasesSelection(caseId) && optEnabled;

  const query = useInfiniteQuery<ContentModerationFindingsPage, Error>({
    queryKey: provenanceKeys.contentModerationFindings(
      cellId,
      folderName,
      detectionType,
      status ?? null,
      limit,
    ),
    queryFn: ({ pageParam, signal }) =>
      fetchContentModerationFindingsPage({
        cellId: cellId as string,
        accessToken: accessToken as string,
        caseId: folderName as string,
        detectionType,
        status,
        limit,
        nextToken: typeof pageParam === 'string' ? pageParam : null,
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? undefined,
    enabled,
    staleTime: 30 * 1000,
  });

  // A disabled query looks settled-and-empty, which on this surface reads as
  // "no flagged sources" and "fully reviewed". See useContentModerationCase.
  return Object.assign(query, { isCaseResolving });
}
